import { useEffect, useState } from 'react'
import { PhotoLightbox } from './PhotoLightbox'
import type { FeedAttachment } from '../lib/feed'
import { signedPhotoUrl } from '../lib/photos'
import { useHubLang } from '../lib/i18n'
import { COMPOSE } from '../lib/hubCopy'

/**
 * The photos on a feed post.
 *
 * ── WHY EVERY TILE HAS ITS BOX BEFORE THE IMAGE ARRIVES ─────────────────────
 *
 * content_attachment stores width and height NOT NULL for this. A grid that
 * sizes itself from the images as they load reflows the whole feed under the
 * reader's thumb — and on a phone on a slow connection, which is the case this
 * hub is built for, that means tapping the wrong thing repeatedly. The
 * aspect-ratio is set from the stored dimensions, so the layout is final before
 * a single byte of image has moved.
 *
 * ── WHY THE URLS ARE FETCHED RATHER THAN STORED ─────────────────────────────
 *
 * The post-media bucket is PRIVATE. A public bucket would make every site
 * photograph readable by URL with no session — the audience model bypassed by a
 * link — and ConeTec's photos are taken on other people's sites. So each render
 * asks Storage to sign a URL, which only succeeds for a session that passes the
 * bucket policy.
 *
 * That means a loading state, and it means a URL can fail. A failed signature
 * renders as a caption saying so rather than a broken-image icon: "this photo
 * did not load" is information, a grey box with a torn-page glyph is not.
 *
 * ── THE LAYOUT IS BY COUNT, NOT BY A GENERIC GRID ───────────────────────────
 *
 * One photo runs full width at its own ratio, because a single site photo IS
 * the post and cropping it to a square throws away the thing being shown. Two
 * or more go to a square-tiled grid, because a row of mixed ratios reads as a
 * mistake. This is the one place a fixed ratio is right: in a grid the tiles
 * have to agree with each other more than they have to agree with the original.
 *
 * ── EVERY TILE OPENS THE VIEWER (141) ───────────────────────────────────────
 *
 * David, 9 Oct: click a photo to see it larger, then move through the post's
 * photos. A grid tile is a crop, so the viewer is the only place the whole
 * photo and its caption are seen. The tile is a real <button>, so it is
 * reachable by keyboard and announced as something that does a thing.
 *
 * ── THE CAPTION ─────────────────────────────────────────────────────────────
 *
 * content_attachment.alt_text is what the composer asks for as a caption. It
 * was only ever an alt attribute, so nobody sighted saw it. Now: under a single
 * photo, and in the viewer for every photo. Not on grid tiles — a caption on a
 * 140px square is three words and an ellipsis. Where it is shown as visible
 * text, the <img> alt is empty so a screen reader does not read it twice.
 */
export function PhotoGrid({
  attachments,
  openPath = null,
}: {
  attachments: FeedAttachment[]
  /** 143. Open the viewer on this photo at once (a photo.tagged bell row). */
  openPath?: string | null
}) {
  const [open, setOpen] = useState<number | null>(() => {
    if (!openPath) return null
    const i = attachments.findIndex((a) => a.path === openPath)
    return i >= 0 ? i : null
  })
  if (attachments.length === 0) return null
  const single = attachments.length === 1

  return (
    <>
      <ul className={`photos photos--${single ? 'one' : 'grid'}`}>
        {attachments.map((a, i) => (
          <li key={a.id} className="photos__item">
            <Photo
              attachment={a}
              index={i}
              count={attachments.length}
              single={single}
              onOpen={() => setOpen(i)}
            />
          </li>
        ))}
      </ul>
      {single && attachments[0].alt && (
        <p className="photos__caption">{attachments[0].alt}</p>
      )}
      {open !== null && (
        <PhotoLightbox
          attachments={attachments}
          index={open}
          onIndex={setOpen}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  )
}

function Photo({
  attachment,
  index,
  count,
  single,
  onOpen,
}: {
  attachment: FeedAttachment
  index: number
  count: number
  single: boolean
  onOpen: () => void
}) {
  const lang = useHubLang()
  const t = COMPOSE[lang]
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    signedPhotoUrl(attachment.path)
      .then((u) => {
        if (!live) return
        if (u) setUrl(u)
        else setFailed(true)
      })
      .catch(() => live && setFailed(true))
    return () => {
      live = false
    }
  }, [attachment.path])

  // The box, held from the stored dimensions. See the header — this is what
  // stops the feed reflowing as images arrive.
  const style = single
    ? { aspectRatio: `${attachment.width} / ${attachment.height}` }
    : undefined

  if (failed) {
    return (
      <div className="photo photo--failed" style={style}>
        <p className="photo__failedtext">{t.photoFailed}</p>
      </div>
    )
  }

  // The button's name carries the caption on a grid tile, where it is not
  // shown; under a single photo it is visible text, so it is not repeated.
  const label =
    !single && attachment.alt
      ? `${t.openPhoto(index + 1, count)}: ${attachment.alt}`
      : t.openPhoto(index + 1, count)

  return (
    <button
      type="button"
      className="photo photo--open"
      style={style}
      onClick={onOpen}
      aria-label={label}
      aria-haspopup="dialog"
    >
      {url && (
        <img
          className="photo__img"
          src={url}
          /* Empty: the button's label names the photo. An empty alt never
             INVENTS a description — the composer asks, it does not make one
             up. */
          alt=""
          width={attachment.width}
          height={attachment.height}
          /* Below the fold on every post but the first. */
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      )}
    </button>
  )
}
